import React, { useState } from 'react';

/**
 * AbilityContainer - Collapsible container for class/ancestry/community abilities
 * Similar to DomainCardContainer but with color theming for ability types
 *
 * @param {string} name - Ability name
 * @param {string} text - Full ability description
 * @param {string} summary - Optional curated summary (shown when collapsed, like domain cards)
 * @param {string} type - 'class' | 'ancestry' | 'community' | 'subclass' | 'hope'
 * @param {string} subtitle - Optional subtitle (e.g., "Sorcerer Feature")
 * @param {React.ReactNode} children - Action buttons (rendered right-justified in header)
 * @param {React.ReactNode} inlineContent - Optional inline content after title (e.g., element selector)
 * @param {React.ReactNode} statusContent - Optional status content below description (e.g., active state)
 */
const AbilityContainer = ({
  name,
  text,
  summary,
  type = 'class',
  subtitle,
  children,
  inlineContent,
  statusContent
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Color themes by ability type
  const themes = {
    class: { title: 'text-yellow-400', subtitle: 'text-yellow-500/70' },
    ancestry: { title: 'text-green-400', subtitle: 'text-green-500/70' },
    community: { title: 'text-amber-300', subtitle: 'text-amber-400/70' },
    subclass: { title: 'text-purple-400', subtitle: 'text-purple-500/70' },
    foundation: { title: 'text-purple-400', subtitle: 'text-purple-500/70' },
    specialization: { title: 'text-cyan-400', subtitle: 'text-cyan-500/70' },
    mastery: { title: 'text-orange-400', subtitle: 'text-orange-500/70' },
    hope: { title: 'text-blue-400', subtitle: 'text-blue-500/70' }
  };

  const theme = themes[type] || themes.class;

  // Generate auto-summary from text if no summary provided
  // Extracts key mechanic info rather than just cutting off text
  const generateSummary = (fullText) => {
    if (!fullText) return '';

    // Look for common patterns to extract key info
    const text = fullText.trim();

    // Pattern: "Mark X Stress to..." or "Spend X Hope to..."
    const costMatch = text.match(/^(Mark \d+ Stress|Spend \d+ Hope) to ([^.]+)/i);
    if (costMatch) {
      return `${costMatch[1]} to ${costMatch[2].toLowerCase()}`;
    }

    // Pattern: "Once per session/rest, ..."
    const onceMatch = text.match(/^(Once per (?:session|short rest|long rest)),?\s*([^.]+)/i);
    if (onceMatch) {
      const action = onceMatch[2].replace(/^you (can|may) /i, '');
      return `${onceMatch[1]}: ${action.charAt(0).toUpperCase() + action.slice(1)}`;
    }

    // Pattern: "When you X, you can/may Y"
    const whenMatch = text.match(/^When (?:you )?([^,]+),\s*(?:you (?:can|may) )?([^.]+)/i);
    if (whenMatch) {
      return `On ${whenMatch[1].toLowerCase()}: ${whenMatch[2]}`;
    }

    // Pattern: "You gain/have X" - just use the gain
    const gainMatch = text.match(/^You (?:gain|have|get) ([^.]+)/i);
    if (gainMatch) {
      return gainMatch[1].charAt(0).toUpperCase() + gainMatch[1].slice(1);
    }

    // Default: first clause up to 100 chars
    const firstClause = text.split(/[.!?,;]/)[0];
    if (firstClause.length <= 100) {
      return firstClause;
    }
    return firstClause.substring(0, 97) + '...';
  };

  // Use provided summary or generate one
  const collapsedText = summary || generateSummary(text);
  const hasMoreText = text && text !== collapsedText;

  return (
    <div className="p-3 bg-gray-800 rounded-lg border border-gray-600">
      {/* Header row: Title + inline content + buttons */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <div className="flex items-center gap-3 flex-grow min-w-0">
          <div className="flex-shrink-0">
            <p className={`font-bold ${theme.title}`}>{name}</p>
            {subtitle && (
              <p className={`text-xs ${theme.subtitle}`}>{subtitle}</p>
            )}
          </div>
          {/* Inline content (e.g., element selector) */}
          {inlineContent && (
            <div className="flex-shrink-0">
              {inlineContent}
            </div>
          )}
        </div>
        {/* Action buttons */}
        {children && (
          <div className="flex-shrink-0">
            {children}
          </div>
        )}
      </div>

      {/* Description - clickable to expand */}
      <div
        onClick={() => hasMoreText && setIsExpanded(!isExpanded)}
        className={`flex items-start gap-1.5 ${hasMoreText ? 'cursor-pointer hover:bg-gray-700/30 -mx-1 px-1 rounded transition-colors' : ''}`}
      >
        <div className="flex-grow">
          <p className={`text-gray-300 text-sm ${isExpanded ? 'whitespace-pre-wrap' : ''}`}>
            {isExpanded ? text : collapsedText}
          </p>
        </div>
        {/* Chevron */}
        {hasMoreText && (
          <span className="flex-shrink-0 text-gray-500">
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        )}
      </div>

      {/* Status content (e.g., active state indicators, token displays) */}
      {statusContent}
    </div>
  );
};

export default AbilityContainer;
